import axios from "axios";
import {
  ASSIGN_UPDATE_TEMPLATE,
  LIST_CONTRACTS_FOR_ADDENDUM,
  LIST_PROJECT_TEMPLATE_URL,
  LIST_STATUS_TEMPLATE,
  UPDATE_TEMPLATE,
  UPLOAD_TEMPLATE_URL,
} from "@/utils/constants/constants.js";
import { validate_file, validate_non_empty_string } from "@/utils/validations/validate_inputs";
function build_project_template_name(template) { return template.name || template.template_name || "Unnamed Template"; }
function build_project_template_type(template) { return String(template.type || template.template_type || "template").toLowerCase(); }
function get_template_modified_at(template, fallback) { return template.updated_at || template.date_modified || fallback; }
function build_project_template_dates(template) {
  const created_at = template.created_at || template.date_uploaded || Date.now();
  const updated_at = get_template_modified_at(template, created_at);
  return {
    date_uploaded: new Date(created_at).toLocaleString(),
    date_modified: new Date(updated_at).toLocaleString(),
  };
}
function build_project_template_links(template) { return { document_url: template.document_url || null, presigned_url: template.presigned_url || null, linked_contracts: [] }; }
function build_project_template_meta(template) { return { version: template.version || 1, entity_type_name: template.entity_type_name || "", template_document_id: template.id, uploaded_by: template.uploaded_by || "N/A" }; }
function build_template_error_message(error) { return `Error fetching templates: ${error.response?.data?.message || error.message}`; }
function map_project_template_record(template, get_file_type) {
  return {
    id: template.id,
    template_name: build_project_template_name(template),
    file_type: get_file_type(template.document_url),
    ...build_project_template_dates(template),
    ...build_project_template_links(template),
    ...build_project_template_meta(template),
    template_type: build_project_template_type(template),
  };
}
function extract_assigned_templates_payload(response) {
  return response.data?.data ?? response.data?.results ?? response.data;
}
function map_assigned_templates_payload(context, raw, entity_type_id) {
  if (entity_type_id) {
    return context.extractTemplatesData(raw).map(assignment => context.mapTemplateAssignment(assignment));
  }
  return (Array.isArray(raw) ? raw : []).map(template => map_project_template_record(template, context._getFileType));
}

export default {
  methods: {
    select_template(template, action = "update") {
      if (!template) return;

      const source = this.manage_template.find(item => item.id === template.id) || template;
      this.selected_template = { ...source };

      if (action === "view") {
        this.preview_modal = true;
        return;
      }

      this.preview_modal = false;
    },
    configure_template(template_entity_type_id) {
      if (!template_entity_type_id) return;
      this.$router.push({
        path: "/configure_template",
        query: {
          template_entity_type_id,
        },
      });
    },
    async handle_file_selection(evt) {
      const file = evt.target.files[0];
      if (!file) {
        this.selected_file = null;
        return;
      }
      return validate_file(file).then((validation) => {
        if (!validation.valid) {
          this.show_error(validation.error);
          evt.target.value = "";
          this.selected_file = null;
          return;
        }
        this.selected_file = file;
      }).catch(() => {
        this.show_error("Error validating file");
        evt.target.value = "";
        this.selected_file = null;
      });
    },
    validate_template_name_input() {
      if (!this.new_template_name) {
        this.show_error("Template name is required.");
        return false;
      }
      if (!validate_non_empty_string(this.new_template_name)) {
        this.show_error("Template name must not be empty");
        return false;
      }
      return true;
    },
    buildUploadFormData() {
      const formData = new FormData();
      formData.append("name", this.new_template_name);
      formData.append("project_id", this.project_id);
      formData.append("document_file", this.selected_file);
      return formData;
    },
    async performUpload(formData) {
      const uploadResponse = await axios.post(UPLOAD_TEMPLATE_URL, formData, {
        headers: { ...this.headers, "Content-Type": "multipart/form-data" },
      });
      if (uploadResponse.data?.status !== "success") {
        throw new Error(uploadResponse.data?.message || "Template upload failed");
      }
      return uploadResponse;
    },
    async performAssignment(templateId) {
      const assignResponse = await axios.post(
        ASSIGN_UPDATE_TEMPLATE,
        { template_id: templateId, entity_type_id: this.entity_type_id, is_assigned: true },
        { headers: this.headers }
      );
      if (assignResponse.data?.status === "success") {
        this.show_success("Template uploaded and assigned successfully");
        await this.get_assigned_templates();
        this.close_upload_modal();
      } else {
        this.show_error("Template uploaded but assignment failed");
      }
    },
    async upload_and_assign_template() {
      if (!this.validate_template_name_input()) return;
      if (!this.selected_file) {
        this.show_error("Please select a file to upload");
        return;
      }

      this.upload_loading = true;
      await this.performUpload(this.buildUploadFormData())
        .then(uploadResponse => this.performAssignment(uploadResponse.data.data.id))
        .catch(error => {
          this.show_error(error.response?.data?.message || error.message || "Error during template upload and assignment");
        }).finally(() => {
          this.upload_loading = false;
        });
    },
    open_upload_modal() {
      this.show_upload_modal = true;
      this.new_template_name = "";
      this.selected_file = null;
      const fileInput = document.querySelector("#template-upload-input");
      if (fileInput) fileInput.value = "";
    },
    close_upload_modal() { this.show_upload_modal = false; },
    async update_template() {
      if (!this.validate_template_name_input()) return;
      this.loading = true;
      const form_data = new FormData();
      form_data.append("template_id", this.selected_template.id);
      if (this.selected_file) form_data.append("new_document_file", this.selected_file);
      form_data.append("new_name", this.selected_template.template_name);
      form_data.append("new_project_id", this.project_id);

      return axios.post(UPDATE_TEMPLATE, form_data, {
        headers: { ...this.headers, "Content-Type": "multipart/form-data" },
      }).then(async (response) => {
        if (response.data?.status !== "success") {
          throw new Error(response.data?.message || "Failed to update template");
        }
        this.show_success("Template updated successfully.");
        await this.get_assigned_templates();
      }).catch((error) => {
        this.show_error(error.response?.data?.message || error.message || "Update failed");
      }).finally(() => {
        this.loading = false;
      });
    },
    _withDefault(val, fallback) { return val || fallback; },
    _getUploadTimestamp(assignment) { return assignment.created_at || new Date().toISOString(); },
    _getModifiedTimestamp(assignment) { return assignment.updated_at || this._getUploadTimestamp(assignment); },
    _getFileType(url) { return url ? url.slice(url.lastIndexOf(".") + 1) : "Unknown File Type"; },
    mapTemplateAssignment(assignment) {
      return {
        id: assignment.id,
        template_name: this._withDefault(assignment.template_name, "Unnamed Template"),
        file_type: this._getFileType(assignment.document_url),
        date_uploaded: new Date(this._getUploadTimestamp(assignment)).toLocaleString(),
        date_modified: new Date(this._getModifiedTimestamp(assignment)).toLocaleString(),
        document_url: this._withDefault(assignment.document_url, null),
        presigned_url: this._withDefault(assignment.presigned_url, null),
        version: this._withDefault(assignment.version, 1),
        entity_type_name: assignment.entity_type_name,
        template_document_id: assignment.template_document,
        template_type: this._withDefault(assignment.template_type?.toLowerCase(), "template"),
        uploaded_by: this._withDefault(assignment.uploaded_by, "N/A"),
        linked_contracts: [],
      };
    },
    extractTemplatesData(raw) { return Array.isArray(raw) ? raw : (Array.isArray(raw?.active_assignments) ? raw.active_assignments : []); },
    async loadLinkedContractsForTemplates() {
      for (const template of this.manage_template) {
        if (template.template_type === "annexure" || template.template_type === "addendum") {
          await this.fetch_linked_contracts(template.id);
        }
      }
    },
    get_template_entity_type_sources() { return [this.entity_type_id, this.$route?.query?.entity_type_id, sessionStorage.getItem("entity_type_id"), sessionStorage.getItem("selected_job_profile_id")]; },
    resolve_template_entity_type_id() { return this.get_template_entity_type_sources().find((value) => value) || null; },
    async apply_assigned_templates_response(response, entity_type_id) {
      const raw = extract_assigned_templates_payload(response);
      this.manage_template = map_assigned_templates_payload(this, raw, entity_type_id);
      await this.loadLinkedContractsForTemplates();
    },
    async get_assigned_templates() {
      this.loading_templates = true;
      const entity_type_id = this.resolve_template_entity_type_id();
      const request = entity_type_id
        ? axios.get(LIST_STATUS_TEMPLATE, {
          headers: this.headers,
          params: { project_id: this.project_id, entity_type_id },
        })
        : axios.get(LIST_PROJECT_TEMPLATE_URL, {
          headers: this.headers,
          params: { project_id: this.project_id },
        });
      return request.then(async (response) => {
        await this.apply_assigned_templates_response(response, entity_type_id);
      }).catch((error) => {
        this.show_error(build_template_error_message(error));
        this.manage_template = [];
      }).finally(() => {
        this.loading_templates = false;
      });
    },
    async fetch_linked_contracts(addendum_id) {
      await axios.get(LIST_CONTRACTS_FOR_ADDENDUM, {
        headers: this.headers,
        params: { addendum_entity_type_id: addendum_id },
      }).then(response => {
        if (response.data?.status !== "success") return;
        const addendum = this.manage_template.find(t => t.id === addendum_id);
        if (!addendum) return;

        addendum.linked_contracts = (response.data.data || []).map(contract => ({
          id: contract.contract_entity_type_id,
          template_name: contract.contract_name,
          template_type: "contract",
          date_uploaded: contract.created_at ? new Date(contract.created_at).toLocaleString() : "",
          date_modified: contract.updated_at ? new Date(contract.updated_at).toLocaleString() : "",
        }));
      }).catch(() => {
        this.show_error("Error fetching linked contracts");
      });
    },
    get_linked_contracts(addendum_id) { return this.manage_template.find(t => t.id === addendum_id)?.linked_contracts || []; },
    toggle_template_expansion(template_id) {
      const index = this.expanded_templates.indexOf(template_id);
      if (index === -1) {
        this.expanded_templates.push(template_id);
      } else {
        this.expanded_templates.splice(index, 1);
      }
    },
    is_template_expanded(template_id) { return this.expanded_templates.includes(template_id); },
    close_the_file_preview() { this.preview_modal = false; },
    is_expandable(template) {
      const type = template.template_type?.toLowerCase();
      return type === "annexure" || type === "addendum";
    },
  },
};
